import { useCallback, useEffect, useRef, useState } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION;
const ENABLED = process.env.NODE_ENV === 'production' && !!BUILD_VERSION;

/**
 * Detecta publicação de nova versão do front comparando /version.json (gravado
 * no build) com a versão embutida no bundle. Nunca recarrega sozinho.
 */
export function useAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (!ENABLED || checking.current) return;
    checking.current = true;
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      if (data && typeof data.version === 'string' && data.version !== BUILD_VERSION) {
        setUpdateAvailable(true);
      }
    } catch (e) {
      // Falha de rede ou JSON inválido: ignora silenciosamente.
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    if (!ENABLED) return undefined;
    check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
    };
  }, [check]);

  const applyUpdate = useCallback(async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) {
      // ignora
    }
    try {
      // Força o cache HTTP do aparelho a trocar o shell antigo ("/" é o start_url do PWA)
      await Promise.all(['/', '/index.html'].map((u) => fetch(u, { cache: 'reload' })));
    } catch (e) {
      // ignora
    }
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      }
    } catch (e) {
      // ignora
    }
    window.location.reload();
  }, []);

  return { updateAvailable, applyUpdate };
}
